'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Eye, EyeOff, X } from 'lucide-react';
import { getSchedulePrefs, setSchedulePrefs } from '@/lib/crew-display';
import MiniMonthCalendar from '@/components/schedule/MiniMonthCalendar';

interface Props {
  value: string | null;
  onChange: (ymd: string) => void;
  placeholder?: string;
  tenantId: string | null;
  /** Default true. Set false for non-scheduling contexts. */
  loadSchedule?: boolean;
}

function pad2(n: number): string { return String(n).padStart(2, '0'); }
function formatYmd(d: Date): string { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }
function todayYmd(): string { return formatYmd(new Date()); }

function fmtFieldLabel(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ScheduleDatePicker({
  value, onChange, placeholder = 'Pick a date',
  tenantId, loadSchedule = true,
}: Props) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<string>(value || todayYmd());
  const fieldRef = useRef<HTMLButtonElement | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const fieldLabel = value ? fmtFieldLabel(value) : placeholder;

  const handleOpen = useCallback(() => {
    setAnchor(value || todayYmd());
    setOpen(true);
  }, [value]);

  const handleClose = useCallback(() => {
    setOpen(false);
    queueMicrotask(() => fieldRef.current?.focus());
  }, []);

  const handlePickDay = useCallback((ymd: string) => {
    onChange(ymd);
    setOpen(false);
    queueMicrotask(() => fieldRef.current?.focus());
  }, [onChange]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, handleClose]);

  const [showNames, setShowNamesState] = useState<boolean>(() =>
    getSchedulePrefs(tenantId).showNames
  );
  const toggleNames = useCallback(() => {
    setShowNamesState(prev => {
      const next = !prev;
      setSchedulePrefs(tenantId, { showNames: next });
      return next;
    });
  }, [tenantId]);

  return (
    <>
      <button
        type="button"
        ref={fieldRef}
        onClick={handleOpen}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Choose date"
        className={`w-full inline-flex items-center justify-between gap-2 px-2 py-1 text-xs rounded-md border bg-[--bg-card] text-[--text-primary] transition-colors ${
          open ? 'border-[#0d8a78] ring-1 ring-[#0d8a78]/40' : 'border-[--border-primary] hover:border-[#0d8a78]/60'
        }`}
      >
        <span className={value ? '' : 'text-[--text-secondary]'}>{fieldLabel}</span>
        <Calendar className="w-3 h-3 text-[#0d8a78]" />
      </button>

      {mounted && open && createPortal(
        <div
          role="dialog"
          aria-label="Calendar"
          onMouseDown={(e) => {
            // Only close if the mousedown lands on the backdrop itself,
            // not on a child (the modal card). e.target === e.currentTarget
            // is the backdrop-vs-card discriminator.
            if (e.target === e.currentTarget) handleClose();
          }}
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
        >
          <div
            onMouseDown={(e) => e.stopPropagation()}
            className="relative bg-[--bg-card] rounded-xl border border-[--border-primary] shadow-2xl w-[min(900px,95vw)] max-h-[95vh] overflow-hidden flex flex-col"
          >
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close calendar"
              className="absolute top-3 right-3 p-1 rounded-md text-[--text-secondary] hover:text-[--text-primary] hover:bg-[--bg-main] z-10"
            >
              <X className="w-4 h-4" />
            </button>
            {loadSchedule && (
              <div className="flex items-center justify-end gap-1 px-3 pt-3">
                <button
                  type="button"
                  onClick={toggleNames}
                  aria-label={showNames ? 'Hide crew names' : 'Show crew names'}
                  title={showNames ? 'Hide crew names' : 'Show crew names'}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md text-[--text-secondary] hover:text-[--text-primary] hover:bg-[--bg-main]"
                >
                  {showNames ? <Eye className="w-3.5 h-3.5 text-[#00C2A8]" /> : <EyeOff className="w-3.5 h-3.5" />}
                  <span>Names</span>
                </button>
              </div>
            )}
            <div className="flex-1 overflow-auto p-2 sm:p-4">
              <MiniMonthCalendar
                tenantId={loadSchedule ? tenantId : null}
                anchor={anchor}
                onPickDay={handlePickDay}
                onMonthChange={setAnchor}
                showHeader
                showLegend={loadSchedule}
                legendEditable={false}
                showNames={loadSchedule && showNames}
                highlightToday
                selectedDateStr={value}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
